(function () {
  'use strict';

  if (customElements.get('bundle-builder')) return;

  class ProductBundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.renderQueued = false;
      this.resetTimer = null;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.addButton = this.querySelector('[data-bundle-add]');
      this.countElement = this.querySelector('[data-bundle-count]');
      this.offerElement = this.querySelector('[data-bundle-offer]');
      this.offerTextElement = this.querySelector('[data-bundle-offer-text]');
      this.statusElement = this.querySelector('[data-bundle-status]');
      this.customizeButton = this.querySelector('[data-bundle-customize-toggle]');

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      document.addEventListener('variant:change', this.onVariantChange);
      document.addEventListener('variantChange', this.onVariantChange);

      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('variant:change', this.onVariantChange);
      document.removeEventListener('variantChange', this.onVariantChange);
      window.clearTimeout(this.resetTimer);
    }

    get items() {
      return Array.from(this.querySelectorAll('[data-bundle-item]'));
    }

    get selectedItems() {
      return this.items.filter((item) => {
        return item.dataset.selected === 'true' && item.dataset.available === 'true';
      });
    }

    onClick(event) {
      const toggle = event.target.closest('[data-bundle-toggle]');
      if (toggle && this.contains(toggle)) {
        const item = toggle.closest('[data-bundle-item]');
        if (!item || item.dataset.required === 'true' || toggle.disabled) return;
        this.setItemSelected(item, item.dataset.selected !== 'true');
        return;
      }

      const customizeToggle = event.target.closest('[data-bundle-customize-toggle]');
      if (customizeToggle && this.contains(customizeToggle)) {
        this.toggleCustomizer();
        return;
      }

      const addButton = event.target.closest('[data-bundle-add]');
      if (addButton && this.contains(addButton)) {
        this.addBundle();
      }
    }

    onChange(event) {
      const select = event.target.closest('[data-bundle-variant-select]');
      if (!select || !this.contains(select)) return;

      const row = select.closest('[data-bundle-customizer-row]');
      const item = this.items.find((candidate) => {
        return candidate.dataset.productId === row.dataset.productId;
      });
      const option = select.options[select.selectedIndex];
      if (!item || !option) return;

      item.dataset.variantId = option.value;
      item.dataset.price = option.dataset.price || '0';
      item.dataset.available = option.dataset.available || 'false';

      const available = item.dataset.available === 'true';
      const toggle = item.querySelector('[data-bundle-toggle]');
      if (toggle) toggle.disabled = !available;

      if (!available) this.setItemSelected(item, false, false);

      const image = item.querySelector('.product-bundle__image');
      if (image && option.dataset.image && image.src !== option.dataset.image) {
        this.swapImage(image, option.dataset.image);
      }

      this.scheduleRender();
    }

    onVariantChange(event) {
      const detail = event.detail || {};
      const variant = detail.variant || (detail.detail && detail.detail.variant);
      if (!variant || !variant.id) return;

      const mainItem = this.items.find((item) => item.dataset.required === 'true');
      if (!mainItem) return;

      const available = variant.available !== false;
      mainItem.dataset.variantId = String(variant.id);
      mainItem.dataset.price = String(variant.price || 0);
      mainItem.dataset.available = String(available);
      mainItem.dataset.selected = String(available);

      const requiredToggle = mainItem.querySelector('.product-bundle__toggle');
      if (requiredToggle) requiredToggle.setAttribute('aria-checked', String(available));

      const imageUrl = this.variantImageUrl(variant) || mainItem.dataset.defaultImage;
      const image = mainItem.querySelector('.product-bundle__image');
      if (image && imageUrl) this.swapImage(image, imageUrl);

      this.scheduleRender();
    }

    setItemSelected(item, selected, shouldAnimate) {
      if (!item || item.dataset.required === 'true') return;
      if (item.dataset.available !== 'true') selected = false;

      item.dataset.selected = String(selected);
      const toggle = item.querySelector('[data-bundle-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-checked', String(selected));
        const productName = this.productNameFor(item.dataset.productId);
        toggle.setAttribute('aria-label', (selected ? 'Remove ' : 'Add ') + productName);
      }

      const row = this.querySelector('[data-bundle-customizer-row][data-product-id="' + item.dataset.productId + '"]');
      if (row) row.dataset.selected = String(selected);

      if (shouldAnimate !== false && toggle && typeof toggle.animate === 'function') {
        toggle.getAnimations().forEach((animation) => animation.cancel());
        toggle.animate(
          [
            { transform: 'scale(.82)' },
            { transform: 'scale(1.12)', offset: .55 },
            { transform: 'scale(1)' }
          ],
          { duration: 280, easing: 'cubic-bezier(.34, 1.56, .64, 1)' }
        );
      }

      this.scheduleRender();
    }

    scheduleRender() {
      if (this.renderQueued) return;
      this.renderQueued = true;
      window.requestAnimationFrame(() => {
        this.renderQueued = false;
        this.render();
      });
    }

    render() {
      const selectedItems = this.selectedItems;
      const count = selectedItems.length;
      const discountPercent = this.discountPercentFor(count);

      if (this.countElement) this.animateText(this.countElement, String(count));
      if (this.offerElement && this.offerTextElement) {
        const offerText = discountPercent > 0 ? discountPercent + '% OFF' : '';
        this.offerElement.hidden = !offerText;
        if (offerText) this.animateText(this.offerTextElement, offerText);
      }

      this.renderRowPrices(selectedItems, discountPercent);

      if (this.addButton && this.dataset.loading !== 'true') {
        this.addButton.disabled = count === 0 || !this.hasRequiredItem();
      }
    }

    discountPercentFor(count) {
      if (this.dataset.discountsEnabled !== 'true') return 0;

      const rawPercent = count >= 3
        ? this.dataset.threeItemDiscount
        : count === 2 ? this.dataset.twoItemDiscount : 0;
      const percent = Number(rawPercent || 0);
      return Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
    }

    renderRowPrices(selectedItems, discountPercent) {
      const selectedProductIds = new Set(selectedItems.map((item) => item.dataset.productId));

      this.items.forEach((item) => {
        const row = this.querySelector('[data-bundle-customizer-row][data-product-id="' + item.dataset.productId + '"]');
        const rowPrice = row && row.querySelector('[data-bundle-row-price]');
        const rowComparePrice = row && row.querySelector('[data-bundle-row-compare-price]');
        const cardPrice = item.querySelector('[data-bundle-card-price]');

        const basePrice = Number(item.dataset.price || 0);
        const isSelected = selectedProductIds.has(item.dataset.productId);
        const hasDiscount = isSelected && discountPercent > 0;
        const displayPrice = hasDiscount
          ? Math.round(basePrice * (100 - discountPercent) / 100)
          : basePrice;

        if (rowComparePrice) {
          rowComparePrice.hidden = !hasDiscount;
          if (hasDiscount) this.animateText(rowComparePrice, this.formatMoney(basePrice));
        }

        if (rowPrice) this.animateText(rowPrice, this.formatMoney(displayPrice));
        if (cardPrice) this.animateText(cardPrice, this.formatMoney(displayPrice));
      });
    }

    hasRequiredItem() {
      return this.items.some((item) => {
        return item.dataset.required === 'true' && item.dataset.available === 'true';
      });
    }

    toggleCustomizer() {
      const expanded = this.dataset.customizing !== 'true';
      this.dataset.customizing = String(expanded);
      if (this.customizeButton) this.customizeButton.setAttribute('aria-expanded', String(expanded));
    }

    async addBundle() {
      if (!this.addButton || this.dataset.loading === 'true') return;

      const items = this.selectedItems.map((item) => ({
        id: Number(item.dataset.variantId),
        quantity: 1,
        properties: {
          _bundle_id: 'product-bundle-' + this.dataset.sectionId
        }
      })).filter((item) => item.id);

      if (!items.length || !this.hasRequiredItem()) return;

      this.setLoading(true);
      this.setStatus('');

      try {
        const response = await fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: items })
        });
        const result = await response.json();

        if (!response.ok || result.status === 422) throw result;

        if (window.BundleOrderAttribution && typeof window.BundleOrderAttribution.mark === 'function') {
          await window.BundleOrderAttribution.mark();
        }

        this.flashAddedState();
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: result,
            quantity: items.length,
            items: items,
            addToCartBtn: this.addButton
          }
        }));

        if (window.theme && theme.settings && theme.settings.cartType === 'page' && theme.routes && theme.routes.cartPage) {
          window.location.assign(theme.routes.cartPage);
        }
      } catch (error) {
        const message = error && (error.description || error.message) || this.dataset.errorLabel;
        this.setStatus(message);
        document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
          detail: { errorMessage: message }
        }));
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      this.dataset.loading = String(loading);
      if (this.addButton) {
        this.addButton.disabled = loading;
        this.addButton.setAttribute('aria-busy', String(loading));
        if (loading) {
          this.addButton.setAttribute('aria-label', this.dataset.addingLabel || 'Adding bundle');
        } else {
          this.addButton.removeAttribute('aria-label');
        }
      }
    }

    flashAddedState() {
      const label = this.querySelector('[data-bundle-add-label]');
      if (!label) return;

      window.clearTimeout(this.resetTimer);
      label.textContent = this.dataset.addedLabel || 'Added';
      this.resetTimer = window.setTimeout(() => {
        label.textContent = this.dataset.addLabel || 'Add Bundle';
      }, 1800);
    }

    setStatus(message) {
      if (this.statusElement) this.statusElement.textContent = message || '';
    }

    animateText(element, nextText) {
      if (!element || element.textContent === nextText) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof element.animate !== 'function') {
        element.textContent = nextText;
        return;
      }

      element.getAnimations().forEach((animation) => animation.cancel());
      const out = element.animate(
        [
          { opacity: 1, transform: 'translateY(0)' },
          { opacity: 0, transform: 'translateY(-4px)' }
        ],
        { duration: 100, easing: 'ease-in', fill: 'forwards' }
      );

      out.finished.then(() => {
        element.textContent = nextText;
        element.animate(
          [
            { opacity: 0, transform: 'translateY(4px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ],
          { duration: 180, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'forwards' }
        );
      }).catch(function () {});
    }

    swapImage(image, nextUrl) {
      if (!image || !nextUrl || image.currentSrc === nextUrl || image.src === nextUrl) return;
      const preload = new Image();
      preload.onload = () => {
        if (typeof image.animate === 'function') {
          image.getAnimations().forEach((animation) => animation.cancel());
          image.animate(
            [
              { opacity: .45, transform: 'scale(.98)' },
              { opacity: 1, transform: 'scale(1)' }
            ],
            { duration: 260, easing: 'cubic-bezier(.22, 1, .36, 1)' }
          );
        }
        image.src = nextUrl;
        image.removeAttribute('srcset');
      };
      preload.src = nextUrl;
    }

    variantImageUrl(variant) {
      const image = variant.featured_image || variant.featured_media || variant.image;
      if (!image) return '';
      return image.src || image.preview_image && image.preview_image.src || '';
    }

    productNameFor(productId) {
      const row = this.querySelector('[data-bundle-customizer-row][data-product-id="' + productId + '"]');
      const name = row && row.querySelector('.product-bundle__customizer-name');
      return name ? name.textContent.trim() : 'product';
    }

    formatMoney(cents) {
      if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
        return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat || this.dataset.moneyFormat);
      }

      const amount = (Number(cents || 0) / 100).toFixed(2);
      return (this.dataset.moneyFormat || '${{amount}}')
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(amount))));
    }
  }

  customElements.define('bundle-builder', ProductBundleBuilder);
})();
